import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { UserPlus, ArrowsClockwise, Sparkle, MagnifyingGlass, Warning, CheckCircle } from "@phosphor-icons/react";

/** Phase 6C — Candidate Assignment / Reassignment / Recommendation.
 *  Admin, Team Lead, HR only. Executive cannot reassign.
 */
const REASONS = ["workload", "leave", "performance", "resignation", "business", "other"];

function AssignModal({ candidate, onClose, onDone }) {
  const [mode, setMode] = useState("manual");
  const [recos, setRecos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState("business");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [allEmps, setAllEmps] = useState([]);
  const [canAssignFlag, setCanAssignFlag] = useState(true);

  const loadRecs = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/candidates/${candidate.id}/recommendations?top=5`);
      setRecos(r.data.recommendations || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load recommendations"); }
    finally { setLoading(false); }
  };
  const loadAll = async () => {
    try {
      // Phase 10 — server-side filtered list based on the actor's assignment scope.
      const r = await api.get("/directory/assignable-owners");
      setCanAssignFlag(!!r.data?.can_assign);
      setAllEmps(r.data?.rows || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadRecs(); loadAll(); }, []);

  const submit = async (submitMode) => {
    setBusy(true);
    try {
      const payload = {
        mode: submitMode,
        employee_id: submitMode === "automatic" ? null : employeeId,
        reason: reason || null,
        reason_code: reasonCode,
        override_capacity: override,
      };
      const endpoint = candidate.assigned_employee_id
        ? `/candidates/${candidate.id}/reassign`
        : `/candidates/${candidate.id}/assign`;
      if (candidate.assigned_employee_id) {
        // reassign body shape
        await api.post(endpoint, {
          employee_id: employeeId, reason: reason || "reassigned", reason_code: reasonCode,
          override_capacity: override,
        });
      } else {
        await api.post(endpoint, payload);
      }
      toast.success(candidate.assigned_employee_id ? "Reassigned" : "Assigned");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50">
      <div className="bg-white rounded-lg p-6 w-[720px] max-h-[90vh] overflow-y-auto space-y-4" data-testid="assign-modal">
        <div>
          <h3 className="font-semibold text-lg">
            {candidate.assigned_employee_id ? "Reassign" : "Assign"} — {candidate.full_name}
          </h3>
          <p className="text-xs text-slate-500">
            Current owner: {candidate.assigned_employee_name || "Unassigned"}
          </p>
        </div>

        <div className="flex gap-2">
          {["manual", "automatic", "recommended"].map((m) => (
            <button key={m} onClick={() => setMode(m)}
                    data-testid={`mode-${m}`}
                    className={`px-3 py-1.5 text-sm rounded-md border ${mode === m ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>
              {m === "manual" ? "Manual" : m === "automatic" ? "Automatic" : "Recommended"}
            </button>
          ))}
        </div>

        {mode === "recommended" && (
          <div className="space-y-2" data-testid="recommendations-panel">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Top 5 Recommendations</div>
              <button onClick={loadRecs} className="text-xs text-slate-500 inline-flex items-center gap-1">
                <ArrowsClockwise size={12} /> Refresh
              </button>
            </div>
            {loading ? <div className="text-sm text-slate-400 p-2">Loading recommendations…</div> : recos.length === 0 ? (
              <div className="text-sm text-amber-700 p-2 bg-amber-50 rounded-md border border-amber-200 inline-flex items-center gap-1">
                <Warning size={14} /> No eligible employee with available capacity.
              </div>
            ) : (
              <div className="space-y-2">
                {recos.map((r) => (
                  <label key={r.employee_id} className={`block border rounded-md px-3 py-2 cursor-pointer ${employeeId === r.employee_id ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}
                         data-testid={`rec-row-${r.employee_id}`}>
                    <input type="radio" name="rec" className="mr-2" checked={employeeId === r.employee_id}
                           onChange={() => setEmployeeId(r.employee_id)} />
                    <span className="font-medium">{r.employee_name}</span>
                    <span className="text-xs text-slate-400 ml-2">{r.employee_code} · {r.team || "—"}</span>
                    <div className="text-xs text-slate-500 mt-1 flex gap-3 flex-wrap">
                      <span>Load: {r.current_load}/{r.max_capacity}</span>
                      <span>Available: <b>{r.available_capacity}</b></span>
                      <span>Success: {r.success_pct}%</span>
                      <span>Offers won: {r.offers_won}</span>
                      <span className="text-emerald-700">Score: <b>{r.score}</b></span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === "manual" && (
          <div>
            <label className="text-xs text-slate-500">Select Employee</label>
            {!canAssignFlag ? (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
                Your role is not permitted to assign candidates.
              </div>
            ) : (
              <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                      data-testid="manual-employee-select"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                <option value="">— select —</option>
                {allEmps.map((e) => (
                  <option key={e.id} value={e.id}>
                    {`${e.name} · ${e.role_label || e.role} · ${e.team || "—"} · ${e.capacity_label}${e.available ? "" : " (full)"}`}
                  </option>
                ))}
              </select>
            )}
            {allEmps.length === 0 && canAssignFlag && (
              <div className="text-xs text-slate-500 mt-1">No eligible assignees in your scope.</div>
            )}
          </div>
        )}

        {mode === "automatic" && (
          <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 inline-flex items-center gap-2">
            <Sparkle size={16} /> The system will pick the top-scoring available Executive.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Reason code *</label>
            <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
                    data-testid="reason-code"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm mt-5">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}
                   data-testid="override-capacity" />
            <span>Override capacity limit</span>
          </label>
        </div>
        <div>
          <label className="text-xs text-slate-500">Reason (details) {candidate.assigned_employee_id && "*"}</label>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    data-testid="assign-reason"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-slate-200 rounded-md">Cancel</button>
          <button onClick={() => submit(mode)}
                  disabled={busy || (mode !== "automatic" && !employeeId) || (candidate.assigned_employee_id && !reason.trim())}
                  data-testid="submit-assign"
                  className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md inline-flex items-center gap-1 disabled:opacity-50">
            <CheckCircle size={14} /> {candidate.assigned_employee_id ? "Reassign" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Assignments() {
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [modalCand, setModalCand] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/candidates?limit=500${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      let rows = r.data || [];
      if (filter === "unassigned") rows = rows.filter((c) => !c.assigned_employee_id);
      if (filter === "assigned") rows = rows.filter((c) => c.assigned_employee_id);
      setCandidates(rows);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const bulkAutoAssign = async () => {
    if (!selected.size) return;
    if (!confirm(`Auto-assign ${selected.size} candidates using recommendation engine?`)) return;
    try {
      const r = await api.post("/candidates/bulk-assign", {
        candidate_ids: [...selected], mode: "automatic",
        reason: "bulk auto-assignment", reason_code: "business",
      });
      const okN = r.data.ok?.length || 0;
      const failN = r.data.failed?.length || 0;
      toast.success(`${okN} assigned, ${failN} failed`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
  };

  const rows = useMemo(() => candidates
    .filter((c) => !q || c.full_name?.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 200), [candidates, q]);

  return (
    <div className="space-y-5" data-testid="assignments-page">
      <div>
        <h2 className="text-2xl font-semibold">Candidate Assignments</h2>
        <p className="text-sm text-slate-500">Assign candidates manually, automatically, or from Top-5 recommendations. Backend enforces capacity + role rules.</p>
      </div>

      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-slate-400" />
          <input placeholder="Search candidates…" value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="assignments-search"
                 className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
                data-testid="assignments-filter"
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="all">All</option>
          <option value="unassigned">Unassigned</option>
          <option value="assigned">Assigned</option>
        </select>
        <button onClick={bulkAutoAssign} disabled={!selected.size}
                data-testid="bulk-auto-assign"
                className="text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-md px-3 py-2 inline-flex items-center gap-1">
          <Sparkle size={14} /> Auto-assign ({selected.size})
        </button>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="px-3 py-2 w-8"><input type="checkbox" onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((c) => c.id)) : new Set())} /></th>
              <th className="text-left px-3 py-2">Candidate</th>
              <th className="text-left px-3 py-2">Placement</th>
              <th className="text-left px-3 py-2">Current Owner</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2 w-40" />
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={6} className="text-center p-6 text-slate-400">Loading…</td></tr>}
            {!busy && rows.length === 0 && <tr><td colSpan={6} className="text-center p-6 text-slate-400">No candidates</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`assign-cand-row-${c.id}`}>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)}
                         data-testid={`assign-cand-check-${c.id}`} />
                </td>
                <td className="px-3 py-2 font-medium">{c.full_name}</td>
                <td className="px-3 py-2 text-slate-500">{c.placement_field || "—"}</td>
                <td className="px-3 py-2 text-slate-500">{c.assigned_employee_name || <span className="text-amber-700">Unassigned</span>}</td>
                <td className="px-3 py-2 text-slate-500">{c.service_status || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setModalCand(c)}
                          data-testid={`assign-btn-${c.id}`}
                          className="text-xs bg-slate-900 text-white rounded-md px-2.5 py-1 inline-flex items-center gap-1 hover:bg-slate-800">
                    <UserPlus size={12} /> {c.assigned_employee_id ? "Reassign" : "Assign"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalCand && <AssignModal candidate={modalCand} onClose={() => setModalCand(null)} onDone={load} />}
    </div>
  );
}
